import { ToastrService as Toastr } from 'ngx-toastr';
import { Injectable } from '@angular/core';
import { LoadService } from 'app/pages/full-pages/load/load.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AbToastr } from 'app/abstract/services/toastr.abstract';

const MESSAGE_OFF = 'message_off';

// Mantém a mensagem do v1, onde o erro genérico do login também virava 401 invalid_grant.
const CHAVES_LOGIN_CREDENCIAL_INVALIDA = ['CREDENCIAIS_INVALIDAS', 'CLIENT_INVALIDO', 'ERRO_AUTENTICACAO'];
const CHAVE_INTEGRADOR_URL_NAO_AUTORIZADO = 'MSG_ACESSO_INTEGRADOR_URL_NAO_AUTORIZADO';

@Injectable({ providedIn: 'root' })
export class ToastrService extends AbToastr {
  constructor(
    public toastr: Toastr,
    private loadService: LoadService,
    private translate: TranslateService,
    private router: Router
  ) {
    super();
  }

  public openAlertByStatus(err: any): string {
    switch (err?.status) {
      case 0:
        return this.translate.instant(
          'Verifique a sua conexão à internet e tente novamente'
        );
      case 401:
        if (err?.error?.error === 'invalid_token')
          return this.translate.instant('Token de acesso inválido.');

        return this.translate.instant('Usuário e/ou senha inválidos.');
      case 400:
      case 406:
        if (err?.error?.error_description === 'Bad credentials')
          return this.openAlertByStatus({ status: 401 });

        if (err?.error?.message) return this.translate.instant(err.error.message);

        // Login v2: o 406 traz só a key; sem este desvio cairia na mensagem genérica abaixo.
        if (CHAVES_LOGIN_CREDENCIAL_INVALIDA.includes(err?.error?.key))
          return this.openAlertByStatus({ status: 401 });

        if (err?.error?.key === CHAVE_INTEGRADOR_URL_NAO_AUTORIZADO)
          return this.translate.instant(CHAVE_INTEGRADOR_URL_NAO_AUTORIZADO);

        // err.error pode não ser JSON (ex.: endpoints com responseType 'text', como o
        // teste de conexão do CFTV) — nesse caso o corpo já é a mensagem de erro.
        try {
          const ex = JSON.parse(err.error);
          return ex?.message;
        } catch {
          return typeof err?.error === 'string' && err.error
            ? err.error
            : this.translate.instant('Erro ao se comunicar com o servidor.');
        }
      case 403:
        return this.translate.instant('Acesso Restrito.');
      case 404:
        return this.translate.instant('Erro ao se comunicar com o servidor.');
      case 500:
        return this.translate.instant('Erro Interno.');
      case 503:
        return this.translate.instant('Serviço temporariamente indisponível.');
      default:
        return err;
    }
  }

  public successMessage(): void {
    this.success('');
  }

  public success(
    title: string,
    message?: string,
    efeitoProgressBar: boolean = true
  ): void {
    this.clear();
    if (message) {
      this.toastr.success(message, title, { progressBar: efeitoProgressBar });
    } else {
      this.toastr.success(this.translate.instant('Salvo com sucesso!'), title, {
        progressBar: efeitoProgressBar,
      });
    }
  }

  public successWithTimeClose(
    title: string,
    message: string,
    efeitoProgressBar: boolean = true
  ): void {
    this.clear();
    this.toastr.success(message, title, {
      timeOut: 6500,
      progressBar: efeitoProgressBar,
    });
  }

  public isErrorCamera(error: any): boolean {
    return (
      ((error.status === 503 || error.status === 401) && error.url && error.url.indexOf('camera.cgi') > 0) ||
      (error.status === 0 && error.error.target.__zone_symbol__xhrURL.indexOf('camera.cgi') > 0) ||
      (error.url && error.url.includes('partner/channels'))
    );
  }

  public error(error: any): void {
    if (this.isErrorCamera(error) || this.isOff()) {
      return;
    }

    this.clear();

    const msg = this.openAlertByStatus(error);
    let type = 'Erro';

    const toastrPosition =
      this.router.url.indexOf('login') > -1
        ? 'toast-top-left'
        : 'toast-top-right';

    if (
      msg === this.translate.instant('Usuário e/ou senha inválidos.') ||
      error.status === 406 ||
      error.status === 0 ||
      msg === this.translate.instant('Token de acesso inválido.') ||
      msg === this.translate.instant('INTEGRADOR_LOGIN_PROBLEM_TOAST_MESSAGE') ||
      msg === this.translate.instant('MSG_ACESSO_INTEGRADOR_URL_NAO_AUTORIZADO')
    ) {
      type = this.translate.instant('ATENCAO');
      this.toastr.warning(msg, type, {
        disableTimeOut: true,
        positionClass: toastrPosition,
      });
      return;
    }
    if (this.toastr.previousToastMessage != msg)
      this.toastr.error(msg, type, {
        disableTimeOut: true,
        positionClass: toastrPosition,
      });
  }

  public errorMessage(error: any): void {
    this.clear();
    this.toastr.error(
      this.openAlertByStatus(error),
      this.translate.instant('Erro'),
      {
        disableTimeOut: true,
      }
    );
  }

  public warning(message: any, clear: boolean = true): void {
    if (clear) this.clear();
    this.toastr.warning(
      this.openAlertByStatus(message),
      this.translate.instant('ATENCAO'),
      {
        disableTimeOut: true,
      }
    );
  }

  public warningWithTimeClose(
    message?: string,
    clear: boolean = true,
    efeitoProgressBar: boolean = true
  ): void {
    if (clear) this.clear();
    this.toastr.warning(
      this.openAlertByStatus(message),
      this.translate.instant('ATENCAO'),
      {
        progressBar: efeitoProgressBar
      }
    );
  }

  public errorWithTimeClose(error: any): void {
    this.clear();
    this.toastr.error(
      this.openAlertByStatus(error),
      this.translate.instant('Erro'),
      {
        timeOut: 6500,
      }
    );
  }

  public erroQr(title: string, message: string): void {
    this.clear();
    this.toastr.error(message, title, {
      timeOut: 3000,
    });
  }
  public infoQr(title: string, message: string): void {
    this.clear();
    this.toastr.info(message, title, {
      timeOut: 3000,
    });
  }

  public info(title: string, message: string): void {
    this.clear();
    this.toastr.info(message, title);
  }

  public infoWithTimeClose(title: string, message: string): void {
    this.clear();
    this.toastr.info(message, title, { timeOut: 6500 });
  }

  public clear() {
    this.loadService.desabilitar();
    this.toastr.clear();
    this.toastr.previousToastMessage = undefined;
  }

  public off() {
    localStorage.setItem(MESSAGE_OFF, 'true');
  }

  public on() {
    localStorage.removeItem(MESSAGE_OFF);
  }

  public isOff(): boolean {
    const value = localStorage.getItem(MESSAGE_OFF);
    return value && value === 'true';
  }

  public isOn(): boolean {
    return !this.isOff();
  }
}
